import { sumBy } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { useList } from 'react-use';
import {
  EventSortByFields,
  EventSortDisplayConfig,
} from 'src/contexts/MobileSortAndFilterContext';
import { useMobileSalesSortAndFilterContext } from 'src/contexts/MobileSortAndFilterContext/MobileSalesSortAndFilterContext';
import {
  getFormattedEventName,
  getPerformerAndVenueForEvent,
} from 'src/utils/eventWithDataUtils';
import {
  getGroupingLabelForString,
  getGroupLabelForDates,
} from 'src/utils/groupingUtils';
import {
  CatalogResults,
  Event,
  EventWithData,
  SaleMetrics,
} from 'src/WebApiController';

type LocalEventGroup = {
  name: string;
  count: number; // item count in the group
  collapsed: boolean; // flag to check if group is collapsed
};

export const useCollapsibleGroups = (
  events: EventWithData[],
  eventMetrics?: Record<string, SaleMetrics>,
  catalogResult?: CatalogResults
) => {
  const [localEventGroups, localEventGroupsMutation] =
    useList<LocalEventGroup>();

  const { sortingCriteria, withEventSortingCriteria } =
    useMobileSalesSortAndFilterContext();

  const sanitizedEvents = useMemo(
    () => withEventSortingCriteria(events ?? [], eventMetrics, catalogResult),
    [catalogResult, eventMetrics, events, withEventSortingCriteria]
  );

  const getEventGroupingText = useCallback(
    (event: Event) => {
      const {
        sorting: { sortBy },
      } = sortingCriteria;

      const { venue, performer } = getPerformerAndVenueForEvent(
        event,
        catalogResult
      );
      const [eventName] = getFormattedEventName({
        event,
        performer,
      });
      if (sortBy === EventSortByFields.EventDate) {
        return getGroupLabelForDates(event.dates?.start);
      }

      if (sortBy === EventSortByFields.VenueName) {
        return getGroupingLabelForString(venue?.name ?? ' ');
      }

      return getGroupingLabelForString(eventName);
    },
    [catalogResult, sortingCriteria]
  );

  useEffect(() => {
    const groups: LocalEventGroup[] = [];
    const configs = sortingCriteria.displayConfig
      .configs as EventSortDisplayConfig[];
    const shouldCollapseAll = configs.includes(
      EventSortDisplayConfig.CollapseAllGroups
    );
    for (const item of sanitizedEvents) {
      const dividerText = getEventGroupingText(item.event);
      const existingGroupIndex = groups.findIndex(
        (group) => group.name === dividerText
      );
      if (existingGroupIndex < 0) {
        groups.push({
          name: dividerText,
          count: 1,
          collapsed: shouldCollapseAll || groups.length > 0,
        });
      } else {
        groups[existingGroupIndex].count += 1;
      }
    }
    localEventGroupsMutation.set(groups);
  }, [
    getEventGroupingText,
    localEventGroupsMutation,
    sanitizedEvents,
    sortingCriteria.displayConfig?.configs,
  ]);

  const updateGroupCollapsed = useCallback(
    (index: number) => (collapsed: boolean) => {
      const currentGroup = localEventGroups[index];
      if (!currentGroup) return;
      localEventGroupsMutation.updateAt(index, {
        ...currentGroup,
        collapsed,
      });
    },
    [localEventGroups, localEventGroupsMutation]
  );

  // Get the event entity from the virtuoso item and group index
  const getEventFromVirtruosoGroup = useCallback(
    (itemIndex: number, groupIndex: number) => {
      const prevGroups = localEventGroups.slice(0, groupIndex);
      const prevCollapsedSum = sumBy(prevGroups, (group) =>
        group.collapsed ? 0 : group.count
      );
      const prevGroupCountSum = sumBy(prevGroups, 'count');

      const eventIndex = prevGroupCountSum + itemIndex - prevCollapsedSum;
      if (eventIndex >= sanitizedEvents.length) {
        return null;
      }
      return sanitizedEvents[eventIndex];
    },
    [localEventGroups, sanitizedEvents]
  );

  return {
    events: sanitizedEvents,
    eventGroups: localEventGroups,
    updateGroupCollapsed,
    getEventFromVirtruosoGroup,
  };
};
